import { useState } from 'react';
import {
  Box, Button, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LineChart } from '@mui/x-charts/LineChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import { formatDay } from '../../utils/dates';
import { isoDate } from '../../utils/format';
import StickyTableContainer from '../table/StickyTableContainer';
import { METRICS, formatAxis, formatMetric, metricValue } from './metrics';

const CHART_METRICS = METRICS.filter((metric) => metric.chart);
const tickFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });

/**
 * Daily trend of one metric (a single series: the title names it, so no legend), with a crosshair tooltip, or the
 * same days as a table. metricId and onMetricChange keep the choice in the page's URL.
 */
export default function TrendChart({ daily, currency, metricId, onMetricChange }) {
  const theme = useTheme();
  const [asTable, setAsTable] = useState(false);
  const metric = CHART_METRICS.find((item) => item.id === metricId) ?? CHART_METRICS[0];
  const dates = daily.map((day) => toDate(day.date));

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between"
             alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>{metric.label} by day</Typography>
          <Typography variant="body2" color="text.secondary">Hover the chart for each day&apos;s value.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <ToggleButtonGroup size="small" exclusive value={metric.id} aria-label="Metric"
                             onChange={(_, value) => value && onMetricChange(value)}>
            {CHART_METRICS.map((item) => (
              <ToggleButton key={item.id} value={item.id} sx={{ px: 1.5, textTransform: 'none' }}>
                {item.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button size="small" onClick={() => setAsTable((value) => !value)}
                  startIcon={asTable ? <ShowChartIcon /> : <TableRowsOutlinedIcon />}>
            {asTable ? 'Show chart' : 'Show table'}
          </Button>
        </Stack>
      </Stack>

      {asTable ? <DailyTable daily={daily} currency={currency} /> : (
        <LineChart
          height={300}
          margin={{ top: 16, right: 16, bottom: 32, left: 64 }}
          xAxis={[{
            data: dates,
            scaleType: 'time',
            valueFormatter: (value, context) => (context.location === 'tick'
              ? tickFormat.format(value) : formatDay(isoDate(value))),
          }]}
          yAxis={[{ valueFormatter: (value) => formatAxis(value, metric, currency) }]}
          series={[{
            data: daily.map((day) => metricValue(day, metric)),
            label: metric.label,
            area: true,
            showMark: false,
            curve: 'linear',
            color: theme.palette.primary.light,
            valueFormatter: (value) => formatMetric(value, metric, currency),
          }]}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={{
            '& .MuiLineElement-root': { strokeWidth: 2 },
            '& .MuiAreaElement-root': { fillOpacity: 0.12 },
            '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
            '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: theme.palette.divider },
            '& .MuiChartsAxis-tickLabel': { fill: theme.palette.text.secondary },
          }}
        />
      )}
    </Paper>
  );
}

function DailyTable({ daily, currency }) {
  return (
    <StickyTableContainer sx={{ maxHeight: 340, border: 1, borderColor: 'divider', borderRadius: 2 }}>
      <Table size="small" stickyHeader aria-label="Daily insights">
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            {METRICS.map((metric) => <TableCell key={metric.id} align="right">{metric.label}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {daily.map((day) => (
            <TableRow key={day.date} hover>
              <TableCell>{formatDay(day.date)}</TableCell>
              {METRICS.map((metric) => (
                <TableCell key={metric.id} align="right">
                  {formatMetric(metricValue(day, metric), metric, currency)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </StickyTableContainer>
  );
}

// YYYY-MM-DD as a local date, so the axis does not shift days across time zones
function toDate(value) {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}
